import {
  type ReactNode,
  createContext,
  useEffect,
  useRef,
  useState,
} from 'react';

export interface IScreenSizeContextData {
  isMobile: boolean;
  windowSize: IWindowSize;
}

interface IWindowSize {
  width: number;
  height: number;
}

interface IScreenSizeProviderProps {
  children: ReactNode;
}

export const ScreenSizeContext = createContext<IScreenSizeContextData>(
  {} as IScreenSizeContextData
);

const DEFAULT_DEBOUNCE_DELAY = 100;
const MOBILE_BREAKPOINT = 600;

export const ScreenSizeProvider = ({
  children,
}: IScreenSizeProviderProps): JSX.Element => {
  const isBrowser = typeof window !== 'undefined';

  const [windowSize, setWindowSize] = useState<IWindowSize>(() => ({
    width: isBrowser ? window.innerWidth : 0,
    height: isBrowser ? window.innerHeight : 0,
  }));

  const [isMobile, setIsMobile] = useState<boolean>(
    () => (isBrowser ? window.innerWidth : 0) < MOBILE_BREAKPOINT
  );

  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isBrowser) return;

    const handleResize = () => {
      if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = setTimeout(() => {
        const width = window.innerWidth;
        const height = window.innerHeight;
        setWindowSize({ width, height });
        setIsMobile(width < MOBILE_BREAKPOINT);
      }, DEFAULT_DEBOUNCE_DELAY);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
    };
  }, [isBrowser]);

  return (
    <ScreenSizeContext.Provider value={{ windowSize, isMobile }}>
      {children}
    </ScreenSizeContext.Provider>
  );
};
